import Vue from 'vue';
import Vuex from 'vuex';

import Article from './services/article';
import UtilityFunctions from './util/classes/UtilityFunctions';


class Deferred {
	constructor() {
		this.promise = new Promise((resolve, reject) => {
			this.reject = reject
			this.resolve = resolve
		})
	}
}

Vue.use(Vuex);

//Not using modularity, because we will only have one module - article.
//For bigger project modules are a better solution - see the VUEX docs
//for pattern suggestions and more.
export default new Vuex.Store({
	state: {
		indexArticles: [],
		articleTree: [],
		articleTypes: [],
		loading: null,
		error: null,
		consoleReady$: new Deferred(),
		authData: {
			tokenData: null,
			user: null,
			isDifferentUser: false,
			isAuthenticated: false,
			isAdmin: false,
			canNavigate: false,
			lastUserId: null,
		}
	},
	getters: {
		filteredIndexArticles: (state) => (filterStr) => {
			let res = state.indexArticles.filter(a => a.published);
			return res.filter(a => a.title.toLowerCase().includes(filterStr.toLowerCase()));
		},
		articlePath: (state) => (id) => {
			let article = state.indexArticles.find(a => a.id == id);
			if (!article) return;

			let type = article.articleType ? UtilityFunctions.normalizeType(article.articleType) : 'Others',
				path = [],
				parentId = article.parentArticleId;

			while (parentId) {
				let art = state.indexArticles.find(a => a.id == parentId);
				if (art) {
					path.unshift({ name: art.title, link: `article-view?id=${art.id}` });
				}
				if(parentId === art.parentArticleId) {
					parentId = null;
					break;
				}
				parentId = art.parentArticleId;
				if (!parentId) {
					type = art.articleType ? UtilityFunctions.normalizeType(art.articleType) : 'Others';
				}
			}
			path.unshift({ name: type, link: 'index' });
			return path;
		},
		treeByType: (state) => (type, isAdmin) => {
			if (isAdmin) {
				return state.articleTree.filter(a => a.articleType == type);
			} else {
				return state.articleTree.filter(a => a.published).filter(a => a.articleType == type);
			}

		},
		allTreesByType: (state, getters) => (isAdmin) => {
			if (state.indexArticles.length == 0 || state.articleTypes.length == 0) return;
			let trees = [];
			let types = [...getters.articleTypes, null];

			types.forEach((type) => {
				let tree = getters.treeByType(type, isAdmin);
				if (tree) {
					trees.push({
						length: UtilityFunctions.calcTreeLength(tree),
						tree,
						name: type ? type : "Others",
					});
				}
			});

			trees = trees.sort((a, b) => b.length - a.length);

			return trees;
		},
		articlesCount: (state) => (isAdmin) => {
			return isAdmin ? state.indexArticles.length : state.indexArticles.filter(a => a.published).length;
		},
		publishedArticlesCount: (state) => {
			return state.indexArticles.filter(a => a.published).length;
		},
		publishedIndexArticleByType: (state) => (type) => {
			return state.indexArticles.filter(a => a.articleType == type && a.published);
		},
		articleTypes: (state) => {
			return state.articleTypes;
		},
		indexArticles: (state) => {
			return state.indexArticles;
		},
		treeArticle: (state) => (id) => {
			return UtilityFunctions.findInTree(id, state.articleTree);
		},
		isAuthenticated: (state) => {
			return state.authData.isAuthenticated;
		},
		canNavigate: (state) => {
			return state.authData.canNavigate;
		},
		isEditor: (state) => {
			return state.authData.isEditor;
		},
		userName: (state) => {
			return state.authData && state.authData.user ? state.authData.user.username : '';
		}
	},
	actions: {
		fetchIndexArticles({ commit }) {
			commit('SET_LOADING', 'index-articles');
			Article.instance().getIndexArticles()
				.then((res) => {
					commit('SET_INDEX_ARTICLES', res.data);
					commit('SET_ARTICLE_TREE', res.data);
					commit('SET_LOADING', null);
				}).catch((err) => {
					commit('SET_ERROR', err);
					commit('SET_LOADING', null);
				});
		},
		fetchArticleTypes({ commit }) {
			commit('SET_LOADING', 'article-types');
			Article.instance().getArticleTypes()
				.then((res) => {
					commit('SET_ARTICLE_TYPES', res.data);
					commit('SET_LOADING', null);
				}).catch((err) => {
					commit('SET_ERROR', err);
					commit('SET_LOADING', null);
				});
		},
		setTokenData({ commit }, tokenData) {
			commit('SET_TOKEN_DATA', tokenData);
		},
		setAuthenticatedUserData({ commit }, data) {
			commit('SET_AUTHENTICATED_USER_DATA', data);
		},
		clearAuthData({ commit }) {
			commit('CLEAR_AUTH_DATA');
		},
		setConsoleReady({ commit }) {
			commit('SET_CONSOLE_READY');
		}
	},
	mutations: {
		SET_INDEX_ARTICLES(state, articles) {
			state.indexArticles = articles;
		},
		SET_LOADING(state, loading) {
			state.loading = loading;
		},
		SET_ERROR(state, error) {
			state.error = error;
		},
		SET_ARTICLE_TYPES(state, types) {
			state.articleTypes = types;
		},
		SET_ARTICLE_TREE(state, articles) {
			articles = UtilityFunctions.sortAlphabetically(articles);
			state.articleTree = UtilityFunctions.buildArticleTree(articles);
		},
		SET_TOKEN_DATA(state, tokenData) {
			state.authData = {
				...state.authData,
				tokenData: tokenData
			};
		},
		SET_AUTHENTICATED_USER_DATA(state, userData) {
			state.authData = {
				...state.authData,
				isAuthenticated: true,
				isDifferentUser: state.authData.lastUserId !== null && state.authData.lastUserId !== userData.user.userId,
				user: userData.user,
				isAdmin: userData.isAdmin,
				isEditor: userData.isEditor,
				canNavigate: userData.canNavigate,
			};
		},
		CLEAR_AUTH_DATA(state) {
			state.authData = {
				...state.authData,
				lastUserId: state.authData.user ? state.authData.user.userId : null,
				tokenData: null,
				user: null,
				isDifferentUser: false,
				isAuthenticated: false,
				isAdmin: false,
				isEditor: false,
				canNavigate: false,
			}
		},
		SET_CONSOLE_READY(state) {
			state.consoleReady$.resolve(true);
		}
	}
})
